import type { ZoneName } from '../content/types';
import type { Fields } from './fields';
import { LotPlanner, type Facing, type Lot } from './lots';
import type { EdgeInfo, PathRoads } from './path-adapter';
import { CELL, type PathEdge } from './paths';
import type { RoadNet } from './roads';
import { hashStr, Rng } from './rng';

/**
 * (track 2) The lot seam, docs/MAPGEN_DECISION.md §6: lots hung off the path network's edges instead of the lattice's lines.
 *
 * A lot stays what every consumer expects — an axis-aligned rectangle with a `facing` of 0–3 (buildings, interiors, driveways
 * and the player-built overlay all assume it; floor plans are tile grids and cannot rotate). What changes is where the
 * rectangles go: along a bent road they *step*, each one set against the road's centre line over its own stretch, so a
 * row of lots hugs the curve like a staircase. The frontage is the edge's chord axis (the adapter's rule: one road, one
 * axis).
 *
 * The network decides nothing about junctions yet — a finer road can leave its own node beside a coarser one, meet it
 * anywhere along its length, or end near it — so the planner trusts geometry alone: the *kerb line* of a row is walked tile
 * by tile against every other road nearby (their scanline tables, array reads, no per-tile queries), a run of clear kerb
 * is a span, a span is cut into lots, and each lot's depth is how far the ground stays free of other roads at its ends,
 * halved so the row across the gap gets the other half. Two lots that still overlap (roads converging within a lot's
 * width) are both dropped — a rule that needs only the pair, so any chunk that sees a lot sees the same verdict, given
 * a margin of a lot's reach around the window. Roads at a steep angle to their chord axis get no lots.
 */

/** the steepest an edge may lean from its chord axis and still carry lots (tan): 0.7 ≈ 35° */
const MAX_LEAN = 0.7;
/** the junction box at an edge's ends stays clear of lots: half a road plus this */
const NODE_CLEAR = 3;
/** (§57, owner Sep 12: "a small radius filled with shops and minimal empty spaces") the inner core: this share of a city's
 *  core radius, where lots pack — wide lots, the corner clear cut to a tile, the building the widest that fits, flush */
const INNER_CORE = 0.6;
/** how far a lot may reach behind its frontage: the padding a window needs so every overlapping pair is seen from both sides */
const REACH = 72;

interface Other { info: EdgeInfo; sameAxis: boolean }

export class PathLots {
  /** the player-built pieces keep their lattice rows: the overlay is pinned to the lattice whichever network runs underneath */
  private readonly built: LotPlanner;
  /**
   * A row's lots, once per (edge, side): a row depends on nothing but the edge and its neighbours, so every window that
   * sees the edge gets the same objects. Without this, each chunk re-planned every row within `REACH` of it — and the
   * planning is the expensive part (the kerb walk samples the terrain per tile), so the town's chunks were spending
   * hundreds of milliseconds re-deciding lots they had already decided (the profile, §28).
   */
  private readonly rows = new Map<string, Lot[]>();
  /** (dev) called with why a row got no lots, or what it got — for tools/dev-lots-diag.ts; unset in the game */
  trace?: (row: string, why: string) => void;
  constructor(readonly fields: Fields, readonly roads: PathRoads, lattice: RoadNet) { this.built = new LotPlanner(fields, lattice); }

  /** All lots whose rectangle intersects [x0,x1)×[y0,y1). Deterministic; any window carves the same lots. */
  lotsIn(x0: number, y0: number, x1: number, y1: number): Lot[] {
    const cand: Lot[] = [];
    const seen = new Set<string>();
    // (the overnight review, Sep 12) the rows are deterministic, so the caches can start over — the path net's own maps do
    if (this.rows.size > 20000) { this.rows.clear(); this.edgeByKey.clear(); this.rowWidth.clear(); }
    const c0 = Math.floor((x0 - REACH) / CELL), c1 = Math.floor((x1 + REACH) / CELL), r0 = Math.floor((y0 - REACH) / CELL), r1 = Math.floor((y1 + REACH) / CELL);
    for (let cy = r0; cy <= r1; cy++) for (let cx = c0; cx <= c1; cx++) {
      for (const e of this.roads.bucket(cx, cy)) {
        if (seen.has(e.key)) continue; seen.add(e.key); this.edgeByKey.set(e.key, e);
        if (e.x1 < x0 - REACH || e.x0 > x1 + REACH || e.y1 < y0 - REACH || e.y0 > y1 + REACH) continue;
        for (const side of [-1, 1] as const) {
          const k = `${e.key}:${side}`;
          let row = this.rows.get(k);
          if (!row) { row = []; this.lotsForEdge(e, side, row); this.rows.set(k, row); }
          for (const l of row) cand.push(l);
        }
      }
    }
    // two lots that overlap: the one on the narrower road gives way at the corner — its frontage is trimmed back to the
    // overlap's edge (a lot is a rectangle, so the trim is along its front); too little left, and it is dropped. Both
    // dropped was the old rule, and downtown it emptied every corner and most of an avenue's frontage (§57). A verdict
    // that needs only the pair, so it is the same from any window; the lots are copied before a trim (rows are cached).
    // the player's own roads: the lattice planner's rows, in the same corner verdict as the network's (a built row has no
    // network width, so it yields at every corner)
    if (this.fields.overlay.roads.size) for (const l of this.built.lotsIn(x0 - REACH, y0 - REACH, x1 + REACH, y1 + REACH)) if (l.planned) cand.push(l);
    cand.sort((a, b) => (a.key < b.key ? -1 : a.key > b.key ? 1 : 0));
    const dead = new Uint8Array(cand.length);
    const trim = (l: Lot, ox0: number, oy0: number, ox1: number, oy1: number): Lot | undefined => {
      const along = l.facing === 0 || l.facing === 2; // the frontage runs along x
      const c: Lot = { ...l };
      if (along) { if (ox0 <= c.x && ox1 < c.x + c.w) { c.w = c.x + c.w - ox1; c.x = ox1; } else if (ox1 >= c.x + c.w && ox0 > c.x) c.w = ox0 - c.x; else return undefined; c.front = c.w; }
      else { if (oy0 <= c.y && oy1 < c.y + c.h) { c.h = c.y + c.h - oy1; c.y = oy1; } else if (oy1 >= c.y + c.h && oy0 > c.y) c.h = oy0 - c.y; else return undefined; c.front = c.h; }
      return c.front >= 3 ? c : undefined;
    };
    const overlaps = (a: Lot, b: Lot): boolean => a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h;
    for (let i = 0; i < cand.length; i++) for (let j = i + 1; j < cand.length; j++) {
      if (dead[i] || dead[j]) continue;
      const a = cand[i], b = cand[j];
      if (!overlaps(a, b)) continue;
      const ox0 = Math.max(a.x, b.x), oy0 = Math.max(a.y, b.y), ox1 = Math.min(a.x + a.w, b.x + b.w), oy1 = Math.min(a.y + a.h, b.y + b.h);
      // the lot on the narrower road yields; the same road, the later key
      const wa = this.widthOfRow(a.key), wb = this.widthOfRow(b.key);
      const yieldA = wa < wb || (wa === wb && a.key > b.key);
      const k = yieldA ? i : j, t = trim(cand[k], ox0, oy0, ox1, oy1);
      if (t) cand[k] = t; else dead[k] = 1;
      if (t && overlaps(t, yieldA ? b : a)) { dead[i] = 1; dead[j] = 1; } // (still overlapping after the trim: both go, as before)
    }
    const out: Lot[] = [];
    for (let i = 0; i < cand.length; i++) { const l = cand[i]; if (!dead[i] && l.x < x1 && l.x + l.w > x0 && l.y < y1 && l.y + l.h > y0) out.push(l); }
    return out;
  }

  /** the painted width of the road a lot fronts, from its key (`plot:<edge>:<side>:<n>`), for the corner rule; a built row is the lattice's */
  private rowWidth = new Map<string, number>();
  private widthOfRow(key: string): number {
    const edgeKey = key.slice(5, key.lastIndexOf(':', key.lastIndexOf(':') - 1));
    let w = this.rowWidth.get(edgeKey); if (w !== undefined) return w;
    w = 0;
    const e = this.edgeByKey.get(edgeKey); if (e) w = this.roads.roadOf(e).total;
    this.rowWidth.set(edgeKey, w); return w;
  }
  private edgeByKey = new Map<string, PathEdge>();
  /** every other road whose box comes near this edge's, with the axis relation the checks below need */
  private others(e: PathEdge, info: EdgeInfo): Other[] {
    const out: Other[] = []; const seen = new Set<string>([e.key]);
    const pad = REACH;
    const c0 = Math.floor((e.x0 - pad) / CELL), c1 = Math.floor((e.x1 + pad) / CELL), r0 = Math.floor((e.y0 - pad) / CELL), r1 = Math.floor((e.y1 + pad) / CELL);
    for (let cy = r0; cy <= r1; cy++) for (let cx = c0; cx <= c1; cx++) for (const o of this.roads.bucket(cx, cy)) {
      if (seen.has(o.key)) continue; seen.add(o.key);
      if (o.x1 < e.x0 - pad || o.x0 > e.x1 + pad || o.y1 < e.y0 - pad || o.y0 > e.y1 + pad) continue;
      const oi = this.roads.roadOf(o); out.push({ info: oi, sameAxis: oi.axis === info.axis });
    }
    return out;
  }

  private lotsForEdge(e: PathEdge, side: 1 | -1, out: Lot[]): void {
    const info = this.roads.roadOf(e);
    const row = `${e.key}:${side}`;
    if (info.road.class === 'rail') { this.trace?.(row, 'rail'); return; } // nothing hangs off a railway
    const vertical = info.axis === 'v';
    const ua = vertical ? e.a.y : e.a.x, ub = vertical ? e.b.y : e.b.x, ta = vertical ? e.a.x : e.a.y, tb = vertical ? e.b.x : e.b.y;
    if (Math.abs(tb - ta) / Math.max(1, Math.abs(ub - ua)) > MAX_LEAN) { this.trace?.(row, `lean ${(Math.abs(tb - ta) / Math.max(1, Math.abs(ub - ua))).toFixed(2)}`); return; }
    // the road's full width, sidewalks included — what `hitsAt` covers and what `LotPlanner.nearEdge` expects (the lattice's `laneWidth`);
    // measured from the carriageway alone the first column of every lot stood on the pavement, and the generator's
    // "a road runs through this footprint" rule then refused the building
    const roadW = info.total, halfOut = Math.floor(roadW / 2), halfIn = Math.floor((roadW - 1) / 2);
    // (§57) inside a city's inner core the lots pack: the corner clear is a tile, the lots are wide, the buildings fill them
    const emx = (e.a.x + e.b.x) / 2, emy = (e.a.y + e.b.y) / 2;
    const tight = this.fields.settlements.near(emx, emy).some((t) => t.size === 'city' && t.core > 0 && Math.hypot(emx - t.x, emy - t.y) < t.core * INNER_CORE);
    const clear = halfOut + (tight ? 1 : NODE_CLEAR);
    const lo = Math.ceil(Math.min(ua, ub) + clear), hi = Math.floor(Math.max(ua, ub) - clear);
    if (hi - lo < 4) { this.trace?.(row, `short ${hi - lo}`); return; }
    // the zone, judged beside the row's middle
    const um = Math.floor((lo + hi) / 2), cm = this.roads.centreAt(info, um); if (cm === undefined) { this.trace?.(row, 'no centre at the middle'); return; }
    const [zx, zy] = vertical ? [cm + side * (halfOut + 6), um] : [um, cm + side * (halfOut + 6)];
    const zone: ZoneName = this.fields.urbanZone(zx, zy);
    if (zone === 'water') { this.trace?.(row, 'water'); return; }
    if (zone === 'rural' && this.fields.density(zx, zy) < 0.02) { this.trace?.(row, 'wilderness'); return; } // wilderness: the road alone
    const z = this.fields.zone(zone);
    const others = this.others(e, info);
    // the kerb line: the first tile beyond the carriageway at row u; blocked where another road's carriageway sits on it,
    // or where the ground is not buildable (water, a cliff)
    const kerb = (u: number): number | undefined => { const c = this.roads.centreAt(info, u); if (c === undefined) return undefined; const pos = Math.round(c); return side === 1 ? pos + halfOut + 1 : pos - halfIn - 1; };
    const roadAt = (u: number, t: number): boolean => { // another road's carriageway on tile (u along, t across)?
      for (const o of others) {
        const w = o.info.total;
        if (o.sameAxis) { const c = this.roads.centreAt(o.info, u); if (c !== undefined && Math.abs(t - Math.round(c)) <= w / 2) return true; }
        else { const c = this.roads.centreAt(o.info, t); if (c !== undefined && Math.abs(u - Math.round(c)) <= w / 2) return true; }
      }
      return false;
    };
    const landAt = (u: number, t: number): boolean => { const tt = vertical ? this.fields.terrainAt(t, u) : this.fields.terrainAt(u, t); return tt.buildable; };
    // How far the ground stays free beyond the kerb at row u, in tiles, up to twice the zone's lot depth. The terrain
    // sample is the expensive part (a noise evaluation per tile; the first chunk of a block spent half a second here,
    // §28c), so the roads are walked first — array reads — and the land only as far as the answer can depend on it:
    // to the nearest other road if there is one within reach, else to the zone's lot depth, beyond which the depth is
    // the zone's anyway. The result is the same as walking every tile.
    const freeMemo = new Map<number, number>();
    const free = (u: number): number => { let f = freeMemo.get(u); if (f === undefined) { f = free0(u); freeMemo.set(u, f); } return f; };
    const free0 = (u: number): number => {
      const k = kerb(u); if (k === undefined) return 0;
      const max = z.lotDepth * 2;
      let road = max;
      for (let d = 0; d < max; d++) if (roadAt(u, k + side * d)) { road = d; break; }
      const landTo = road < max ? road : z.lotDepth;
      for (let d = 0; d < landTo; d++) if (!landAt(u, k + side * d)) return d;
      return road; // (max when no road is within reach)
    };
    // the span scan only asks whether the first two tiles beyond the kerb are clear: two samples, not a walk
    const clear2 = (u: number): boolean => {
      const f = freeMemo.get(u); if (f !== undefined) return f >= 2;
      const k = kerb(u); if (k === undefined) return false;
      for (let d = 0; d < 2; d++) { const t = k + side * d; if (roadAt(u, t) || !landAt(u, t)) return false; }
      return true;
    };
    // the row's lot boundaries are fixed by the edge alone — not by the road's width, nor by where the clear spans fall —
    // so a road that widens keeps its lots where they were and they only step back (Q74, docs/MAPGEN_DECISION.md §17:
    // depth is the give, frontage is not; and a city that widens a street in play must not reshuffle the property on it)
    const rowKey = `plot:${e.key}:${side}`;
    const rng = new Rng(hashStr(rowKey, this.fields.seed));
    const uStart = Math.ceil(Math.min(ua, ub)), uEnd = Math.floor(Math.max(ua, ub));
    const cuts: number[] = [uStart];
    // a lot is one rectangle, so on a bend it can only stand as far back as the road's furthest reach over its own stretch;
    // a wide lot on a tight bend leaves a wedge of empty ground at one end. So a lot is cut narrower until the road's
    // centre wanders no more than a tile across it — still a function of the edge alone, so a widened road keeps the cuts
    const spread = (a: number, b: number): number => { let lo = Infinity, hi = -Infinity; for (let u = a; u < b; u++) { const c = this.roads.centreAt(info, u); if (c === undefined) continue; if (c < lo) lo = c; if (c > hi) hi = c; } return hi - lo; };
    for (let c = uStart; c < uEnd; ) {
      let w = tight ? rng.int(Math.max(z.lotWidthMin, Math.min(8, z.lotWidthMax)), z.lotWidthMax) : rng.int(z.lotWidthMin, z.lotWidthMax); // (tight: wide lots, for the wide buildings that fill them)
      while (w > z.lotWidthMin && spread(c, c + w) > 1) w--;
      c = Math.min(uEnd, c + w); cuts.push(c);
    }
    const facing: Facing = vertical ? (side === 1 ? 1 : 3) : side === 1 ? 2 : 0;
    const place = (cur: number, w: number, n: number): void => {
      // the depth: the least free ground over the lot's stretch, halved so the row across the gap gets its half —
      // whole if the block behind is empty; the near edge beyond the road's furthest reach over the stretch
      let d = Infinity, reach = side === 1 ? -Infinity : Infinity, widen = 0;
      for (const uu of [cur, cur + Math.floor(w / 2), cur + w - 1]) {
        const f = free(uu), other = f < z.lotDepth * 2 && roadAt(uu, (kerb(uu) ?? 0) + side * f);
        d = Math.min(d, other ? Math.floor(f / 2) : f);
      }
      // (owner, Sep 14: "some buildings' corner is just on the edge of the road") the near edge from every row of the lot, not three,
      // and from the band as it lies across the grid: a road at a slant covers more of a row than its width — total / cos, from the
      // slope of the centres either side — so a lot set by the width alone put its corner on the pavement of a diagonal road
      for (let uu = cur - 1; uu <= cur + w; uu++) {
        const c = this.roads.centreAt(info, uu); if (c === undefined) continue;
        const ca = this.roads.centreAt(info, uu - 1), cb = this.roads.centreAt(info, uu + 1);
        const slope = ca !== undefined && cb !== undefined ? (cb - ca) / 2 : ca !== undefined ? c - ca : cb !== undefined ? cb - c : 0;
        // what a slant adds to the band's half-width across a row, over the straight road's (zero on a straight road)
        widen = Math.max(widen, (roadW / 2) * (Math.sqrt(1 + slope * slope) - 1));
        reach = side === 1 ? Math.max(reach, c) : Math.min(reach, c);
      }
      if (!Number.isFinite(reach)) return;
      // the lot starts beyond the slanted band; a straight road pushes it nowhere (the lots there stay flush to the pavement, where
      // they always were), and what a slant pushes out comes off the lot's depth, so its back edge stays where the row behind expects
      const push = Math.max(0, Math.ceil(widen - 0.25));
      const depth = Math.min(z.lotDepth, d) - push;
      if (depth < 2) return;
      const flat = LotPlanner.nearEdge(Math.round(reach), roadW, side, depth);
      const near = side === 1 ? flat + push : flat - push;
      out.push(vertical
        ? { key: `${rowKey}:${n}`, x: near, y: cur, w: depth, h: w, facing, zone, front: w, depth, ...(tight ? { tight: true as const } : {}) }
        : { key: `${rowKey}:${n}`, x: cur, y: near, w, h: depth, facing, zone, front: w, depth, ...(tight ? { tight: true as const } : {}) });
    };
    // spans of clear kerb; each takes the boundaries that fall inside it, and a clipped end piece too small to be a lot
    // joins its neighbour in the span
    let u = lo;
    while (u < hi) {
      while (u < hi && !clear2(u)) u++;
      let end = u; while (end < hi && clear2(end)) end++;
      if (end - u >= z.lotWidthMin) {
        const pieces: { cur: number; w: number; n: number }[] = [];
        for (let i = 0; i + 1 < cuts.length; i++) {
          const a = Math.max(u, cuts[i]), b = Math.min(end, cuts[i + 1]);
          if (b - a > 0) pieces.push({ cur: a, w: b - a, n: i });
        }
        if (pieces.length > 1 && pieces[0].w < z.lotWidthMin) { const p = pieces.shift()!; pieces[0].cur = p.cur; pieces[0].w += p.w; }
        if (pieces.length > 1 && pieces[pieces.length - 1].w < z.lotWidthMin) { const p = pieces.pop()!; pieces[pieces.length - 1].w += p.w; }
        for (const p of pieces) if (p.w >= 2) place(p.cur, p.w, p.n);
      }
      u = Math.max(end, u + 1);
    }
    if (this.trace) { let f2 = 0; for (let uu = lo; uu < hi; uu++) if (clear2(uu)) f2++; this.trace(row, `rows ${lo}..${hi} (${hi - lo}), kerb free≥2 on ${f2}, lots ${out.length}`); }
  }
}
